import React, { useState } from 'react';

const SolarCalculator = () => {
  const [step, setStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [formData, setFormData] = useState({
    monthlyBill: 150,
    roofOrientation: 'sud',
    withBattery: null,
    prenom: '',
    telephone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Paramètres de calcul validés avec LLDLS
  const PARAMS = {
    electricityPrice: 0.25,        // €/kWh
    gridInjectionPrice: 0.08,      // €/kWh
    productionPerKwc: 900,         // kWh/an par kWc
    costWithoutBattery: 1250,      // €/kWc
    costWithBattery: 1500,         // €/kWc
    selfConsumptionNoBattery: 0.30,  // 30%
    selfConsumptionWithBattery: 0.70, // 70%
    co2PerKwh: 0.33                // kg CO2/kWh
  };

  // Facteurs d'orientation
  const orientationFactors = {
    'sud': { factor: 1.0, label: 'Sud', icon: '☀️' },
    'sud-est-ouest': { factor: 0.95, label: 'Sud-Est / Sud-Ouest', icon: '🌤️' },
    'est-ouest': { factor: 0.85, label: 'Est / Ouest', icon: '⛅' },
    'nord': { factor: 0.60, label: 'Nord', icon: '☁️' }
  };

  // Calculs
  const calculateResults = () => {
    const annualBill = formData.monthlyBill * 12;
    const annualConsumption = annualBill / PARAMS.electricityPrice;
    const orientationFactor = orientationFactors[formData.roofOrientation].factor;
    
    // Taille recommandée (ajustée selon orientation)
    const baseKwc = annualConsumption / PARAMS.productionPerKwc;
    const recommendedKwc = Math.ceil(baseKwc / orientationFactor);
    
    // Production réelle
    const annualProduction = recommendedKwc * PARAMS.productionPerKwc * orientationFactor;
    
    // Calculs SANS batterie
    const selfConsumptionNo = annualProduction * PARAMS.selfConsumptionNoBattery;
    const injectedNo = annualProduction - selfConsumptionNo;
    const savingsNo = (selfConsumptionNo * PARAMS.electricityPrice) + (injectedNo * PARAMS.gridInjectionPrice);
    const costNo = recommendedKwc * PARAMS.costWithoutBattery;
    const paybackNo = costNo / savingsNo;
    
    // Calculs AVEC batterie
    const selfConsumptionYes = annualProduction * PARAMS.selfConsumptionWithBattery;
    const injectedYes = annualProduction - selfConsumptionYes;
    const savingsYes = (selfConsumptionYes * PARAMS.electricityPrice) + (injectedYes * PARAMS.gridInjectionPrice);
    const costYes = recommendedKwc * PARAMS.costWithBattery;
    const paybackYes = costYes / savingsYes;
    
    // CO2 évité
    const co2Avoided = (annualProduction * PARAMS.co2PerKwh) / 1000; // en tonnes
    
    return {
      annualConsumption: Math.round(annualConsumption),
      recommendedKwc,
      annualProduction: Math.round(annualProduction),
      co2Avoided: co2Avoided.toFixed(1),
      withoutBattery: {
        savings: Math.round(savingsNo),
        cost: Math.round(costNo),
        payback: paybackNo.toFixed(1)
      },
      withBattery: {
        savings: Math.round(savingsYes),
        cost: Math.round(costYes),
        payback: paybackYes.toFixed(1)
      }
    };
  };

  const results = calculateResults();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Créer le lead avec les données de la simulation
      const leadData = {
        prenom: formData.prenom,
        nom: '',
        email: formData.email,
        telephone: formData.telephone,
        code_postal: '',
        type_projet: 'Panneaux Solaires',
        type_bien: '',
        surface: '',
        urgence: 'Moyen terme',
        source: 'Calculateur Solaire',
        notes: `SIMULATION SOLAIRE:
- Facture mensuelle: ${formData.monthlyBill}€
- Orientation toit: ${orientationFactors[formData.roofOrientation].label}
- Option batterie: ${formData.withBattery ? 'Oui' : 'Non'}
- Installation recommandée: ${results.recommendedKwc} kWc
- Production estimée: ${results.annualProduction} kWh/an
- Économies estimées: ${formData.withBattery ? results.withBattery.savings : results.withoutBattery.savings}€/an
- Rentabilité: ${formData.withBattery ? results.withBattery.payback : results.withoutBattery.payback} ans`
      };

      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });

      if (response.ok) {
        setSubmitted(true);
        // Google Tag Manager event
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'calculator_lead_submitted',
            calculator_type: 'solar',
            recommended_kwc: results.recommendedKwc,
            with_battery: formData.withBattery
          });
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  return (
    <section id="calculateur" className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-amber-100 text-amber-800 text-sm font-medium px-4 py-1 rounded-full mb-4">
            ☀️ Simulateur gratuit
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Calculez vos économies solaires
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez en 2 minutes combien vous pourriez économiser avec des panneaux solaires
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Progress Bar */}
          {!showResults && (
            <div className="bg-gray-100 h-2">
              <div 
                className="bg-amber-500 h-2 transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          )}

          <div className="p-6 sm:p-10">
            
            {!showResults ? (
              <>
                {/* Step 1: Monthly Bill */}
                {step === 1 && (
                  <div className="space-y-8" data-testid="calculator-step-1">
                    <div className="text-center">
                      <span className="text-sm text-gray-500 mb-2 block">Étape 1/3</span>
                      <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                        Quelle est votre facture d'électricité mensuelle ?
                      </h3>
                    </div>
                    
                    <div className="space-y-6">
                      <div className="text-center">
                        <span className="text-5xl sm:text-6xl font-bold text-amber-600">
                          {formData.monthlyBill}€
                        </span>
                        <span className="text-gray-500 text-lg">/mois</span>
                      </div>
                      
                      <input
                        type="range"
                        min="50"
                        max="400"
                        step="10"
                        value={formData.monthlyBill}
                        onChange={(e) => setFormData({...formData, monthlyBill: parseInt(e.target.value)})}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
                        data-testid="monthly-bill-slider"
                      />
                      
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>50€</span>
                        <span>400€</span>
                      </div>
                    </div>

                    <button
                      onClick={nextStep}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors text-lg"
                      data-testid="calculator-next-btn"
                    >
                      Continuer →
                    </button>
                  </div>
                )}

                {/* Step 2: Roof Orientation */}
                {step === 2 && (
                  <div className="space-y-8" data-testid="calculator-step-2">
                    <div className="text-center">
                      <span className="text-sm text-gray-500 mb-2 block">Étape 2/3</span>
                      <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                        Quelle est l'orientation de votre toit ?
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(orientationFactors).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => setFormData({...formData, roofOrientation: key})}
                          className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
                            formData.roofOrientation === key
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-amber-300'
                          }`}
                          data-testid={`orientation-${key}`}
                        >
                          <span className="text-2xl sm:text-3xl mb-2 block">{value.icon}</span>
                          <span className="font-medium text-gray-900 text-sm sm:text-base">{value.label}</span>
                          <span className="text-xs sm:text-sm text-gray-500 block mt-1">
                            {Math.round(value.factor * 100)}% rendement
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="flex gap-4">
                      <button
                        onClick={prevStep}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                      >
                        ← Retour
                      </button>
                      <button
                        onClick={nextStep}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors"
                        data-testid="calculator-next-btn"
                      >
                        Continuer →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Battery Option */}
                {step === 3 && (
                  <div className="space-y-8" data-testid="calculator-step-3">
                    <div className="text-center">
                      <span className="text-sm text-gray-500 mb-2 block">Étape 3/3</span>
                      <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                        Souhaitez-vous une batterie de stockage ?
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        onClick={() => {
                          setFormData({...formData, withBattery: false});
                          setShowResults(true);
                        }}
                        className="p-6 rounded-xl border-2 border-gray-200 hover:border-amber-300 transition-all text-left"
                        data-testid="battery-no"
                      >
                        <span className="text-3xl mb-3 block">⚡</span>
                        <span className="font-semibold text-gray-900 text-lg block mb-2">Sans batterie</span>
                        <span className="text-sm text-gray-500">
                          Investissement plus faible, idéal si vous êtes souvent présent la journée
                        </span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setFormData({...formData, withBattery: true});
                          setShowResults(true);
                        }}
                        className="p-6 rounded-xl border-2 border-gray-200 hover:border-amber-300 transition-all text-left relative overflow-hidden"
                        data-testid="battery-yes"
                      >
                        <span className="absolute top-2 right-2 bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                          Recommandé
                        </span>
                        <span className="text-3xl mb-3 block">🔋</span>
                        <span className="font-semibold text-gray-900 text-lg block mb-2">Avec batterie</span>
                        <span className="text-sm text-gray-500">
                          Autonomie maximale, rentabilité optimisée avec le tarif dynamique
                        </span>
                      </button>
                    </div>

                    <button
                      onClick={prevStep}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-colors"
                    >
                      ← Retour
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Results Section */
              <div className="space-y-8" data-testid="calculator-results">
                {!submitted ? (
                  <>
                    <div className="text-center">
                      <span className="text-4xl mb-4 block">🎉</span>
                      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Votre simulation personnalisée
                      </h3>
                      <p className="text-gray-600">
                        Basée sur une facture de {formData.monthlyBill}€/mois, orientation {orientationFactors[formData.roofOrientation].label.toLowerCase()}
                      </p>
                    </div>

                    {/* Results Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <span className="text-amber-600 text-2xl sm:text-3xl font-bold block">
                          {results.recommendedKwc}
                        </span>
                        <span className="text-gray-600 text-sm">kWc recommandé</span>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <span className="text-green-600 text-2xl sm:text-3xl font-bold block">
                          {formData.withBattery ? results.withBattery.savings : results.withoutBattery.savings}€
                        </span>
                        <span className="text-gray-600 text-sm">économies/an</span>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <span className="text-blue-600 text-2xl sm:text-3xl font-bold block">
                          {formData.withBattery ? results.withBattery.payback : results.withoutBattery.payback}
                        </span>
                        <span className="text-gray-600 text-sm">ans rentabilité</span>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-4 text-center">
                        <span className="text-emerald-600 text-2xl sm:text-3xl font-bold block">
                          {results.co2Avoided}t
                        </span>
                        <span className="text-gray-600 text-sm">CO₂ évité/an</span>
                      </div>
                    </div>

                    {/* Comparison */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="font-semibold text-gray-900 mb-4 text-center">Comparaison des options</h4>
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className={`p-4 rounded-lg ${!formData.withBattery ? 'bg-amber-100 ring-2 ring-amber-500' : 'bg-white'}`}>
                          <span className="text-2xl block mb-2">⚡</span>
                          <span className="font-medium block">Sans batterie</span>
                          <span className="text-gray-600 text-sm block">{results.withoutBattery.cost.toLocaleString()}€</span>
                          <span className="text-green-600 font-semibold block">{results.withoutBattery.savings}€/an</span>
                          <span className="text-gray-500 text-sm">{results.withoutBattery.payback} ans</span>
                        </div>
                        <div className={`p-4 rounded-lg ${formData.withBattery ? 'bg-amber-100 ring-2 ring-amber-500' : 'bg-white'}`}>
                          <span className="text-2xl block mb-2">🔋</span>
                          <span className="font-medium block">Avec batterie</span>
                          <span className="text-gray-600 text-sm block">{results.withBattery.cost.toLocaleString()}€</span>
                          <span className="text-green-600 font-semibold block">{results.withBattery.savings}€/an</span>
                          <span className="text-gray-500 text-sm">{results.withBattery.payback} ans</span>
                        </div>
                      </div>
                    </div>

                    {/* Lead Capture Form */}
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
                      <h4 className="font-semibold text-lg mb-2 text-center">
                        Recevez votre étude personnalisée gratuite
                      </h4>
                      <p className="text-amber-100 text-sm text-center mb-4">
                        Un expert LLDLS vous contacte sous 24h pour affiner cette simulation
                      </p>
                      
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <input
                          type="text"
                          placeholder="Votre prénom"
                          required
                          value={formData.prenom}
                          onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                          data-testid="calculator-prenom"
                        />
                        <input
                          type="tel"
                          placeholder="Votre téléphone"
                          required
                          value={formData.telephone}
                          onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                          data-testid="calculator-telephone"
                        />
                        <input
                          type="email"
                          placeholder="Votre email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                          data-testid="calculator-email"
                        />
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-white text-amber-600 font-semibold py-4 px-6 rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50"
                          data-testid="calculator-submit-btn"
                        >
                          {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon étude gratuite →'}
                        </button>
                      </form>
                    </div>

                    {/* Disclaimer */}
                    <p className="text-xs text-gray-500 text-center">
                      * Cette simulation est une estimation basée sur les données moyennes en Wallonie. 
                      Pour un calcul précis adapté à votre situation, nos experts réalisent une étude gratuite sur place.
                    </p>

                    {/* Restart */}
                    <button
                      onClick={() => {
                        setShowResults(false);
                        setStep(1);
                        setFormData({...formData, withBattery: null});
                      }}
                      className="w-full text-gray-500 hover:text-gray-700 text-sm py-2"
                    >
                      ← Recommencer la simulation
                    </button>
                  </>
                ) : (
                  /* Success Message */
                  <div className="text-center py-8" data-testid="calculator-success">
                    <span className="text-6xl block mb-4">✅</span>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Merci {formData.prenom} !
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Votre simulation a bien été envoyée. Un expert LLDLS vous contactera sous 24h pour discuter de votre projet.
                    </p>
                    <div className="bg-gray-50 rounded-xl p-6 text-left max-w-md mx-auto">
                      <h4 className="font-semibold text-gray-900 mb-3">Récapitulatif de votre simulation :</h4>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li>• Installation recommandée : <strong>{results.recommendedKwc} kWc</strong></li>
                        <li>• Économies annuelles : <strong>{formData.withBattery ? results.withBattery.savings : results.withoutBattery.savings}€</strong></li>
                        <li>• Rentabilité : <strong>{formData.withBattery ? results.withBattery.payback : results.withoutBattery.payback} ans</strong></li>
                        <li>• Option : <strong>{formData.withBattery ? 'Avec batterie' : 'Sans batterie'}</strong></li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolarCalculator;
